import * as React from 'react';

import { Image, IImageProps, Anchor, IAnchorProps, Heading, THeadingLevel, Text } from 'telia-front-react';

export interface IProductPrice {
  /**
   * Price text.
   */
  label?: string;
  /**
   * Value text.
   */
  value?: string;
  /**
   * Original price text.
   */
  originalPrice?: string;
}

export interface IProductLink extends Omit<IAnchorProps, 'children'> {
  /**
   * Link text.
   */
  text: string;
}

export interface IProductProps {
  /**
   * Name prop.
   */
  name: string;
  /**
   * Brand name.
   */
  brand: string;
  /**
   * Image props, see @image.
   */
  image: IImageProps;
  /**
   * Additional class name.
   */
  className?: string;
  /**
   * Price props.
   */
  price?: IProductPrice;
  /**
   * Price props.
   */
  monthlyPrice?: IProductPrice;
  /**
   * Link props, see @anchor.
   */
  link?: IProductLink;
  /**
   * Gift link props, see @anchor.
   */
  giftLink?: IProductLink;
  /**
   * Cta props.
   */
  cta?: string;
  /**
   * Level of heading. Defaults to 3
   */
  headingLevel?: THeadingLevel;
}

export interface IProductDefaultProps {
  headingLevel: IProductProps['headingLevel'];
}

export default class Product extends React.Component<IProductProps> {
  static defaultProps: IProductDefaultProps = {
    headingLevel: 3
  };

  renderImage = (): JSX.Element => {
    const { image } = this.props;

    return <Image {...image} />;
  };

  renderPrice = (price: IProductPrice | undefined): JSX.Element | null => {
    if (!price) {
      return null;
    }

    const { label, value, originalPrice } = price;

    return (
      <Text>
        <p className="h4">
          {label && <span>{`${label} `}</span>}
          {label && <br />}
          {value && <span className="text-bold">{`${value} `}</span>}
        </p>

        {originalPrice && <p className="text-small text-light">{originalPrice}</p>}
      </Text>
    );
  };

  renderLink = (link: IProductLink | undefined): JSX.Element | null => {
    if (!link) {
      return null;
    }

    const { text, ...rest } = link;

    return (
      <p className="text-small">
        <Anchor {...rest}>{text}</Anchor>
      </p>
    );
  };

  render(): JSX.Element {
    const { brand, name, cta, link, giftLink, price, monthlyPrice } = this.props;

    return (
      <div>
        <div className="product__figure">{this.renderImage()}</div>

        <div className="product__content text">
          <div className="product__content-top">
            <Heading level={this.props.headingLevel} className="h4 text-bold">
              {brand}
              <br />
              {name}
            </Heading>
          </div>

          <div className="product__content-bottom text">
            {this.renderPrice(price)}

            {this.renderPrice(monthlyPrice)}

            {cta && <p>{cta}</p>}

            {this.renderLink(link)}

            {this.renderLink(giftLink)}
          </div>
        </div>
      </div>
    );
  }
}
